export interface IconPosition {
    x: number;
    y: number;
}

export interface AnimationState {
    x: number;
    y: number;
    originalY: number;
    rotation: number;
}

export type UpdatePositionFn = (state: AnimationState, drawerCtx: SpecialEffectDrawer) => void;

export class SpecialEffectDrawer {
    private canvas: HTMLCanvasElement | null = null;
    private ctx: CanvasRenderingContext2D | null = null;
    private animationStates: AnimationState[] = [];
    private animationFrameId?: number;
    private imgCache: Map<string, HTMLImageElement> = new Map();
    public size: number;
    public speed: number;
    private rotationAmplitude: number; // 旋转幅度（角度）
    private rotationSpeed: number; // 旋转速度
    private startTime = 0;
    private animationDuration = 0;
    private lastDrawTime = 0;
    private readonly frameInterval: number = 1000 / 60;
    private updatePosition: UpdatePositionFn;
    private onAnimationEnd?: () => void;
    public offscreenCanvas: HTMLCanvasElement | null = null;
    public offscreenCtx: CanvasRenderingContext2D | null = null;

    public cachedImage: HTMLImageElement | null = null;
    public constructor(canvas: HTMLCanvasElement | null = null, size = 80, speed = 1.7) {
        this.rotationAmplitude = 15; // 最大旋转角度为15度
        this.rotationSpeed = 0.01; // 旋转速度
        this.size = size;
        this.speed = speed * 2;
        if (canvas) {
            this.setCanvas(canvas);
        }
        this.updatePosition = (state: AnimationState): void => {
            state.y -= this.speed;
            // 计算旋转角度
            const currentTime = performance.now();
            state.rotation = Math.sin(currentTime * this.rotationSpeed) * this.rotationAmplitude;
            if (state.y < -this.size && this.canvas) {
                state.y = this.canvas.height;
            }
            // 如果有上下文，直接在这里处理绘制逻辑
            if (this.offscreenCtx && this.cachedImage) {
                // 保存当前上下文状态
                this.offscreenCtx.save();

                // 移动到图像中心点
                this.offscreenCtx.translate(state.x + this.size / 2, state.y + this.size / 2);

                // 应用旋转
                this.offscreenCtx.rotate((state.rotation * Math.PI) / 180);

                // 绘制图像（需要将坐标原点移回左上角）
                this.offscreenCtx.drawImage(
                    this.cachedImage,
                    -this.size / 2,
                    -this.size / 2,
                    this.size,
                    this.size,
                );
                this.offscreenCtx.restore();
            }
        };
    }

    /**
     * 函数内部方法, 用于预加载图片
     * @param {string} imgSrc 图片的地址
     */
    private async preloadImage(imgSrc: string): Promise<HTMLImageElement> {
        if (this.imgCache.has(imgSrc)) {
            return this.imgCache.get(imgSrc)!;
        }

        return new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                this.imgCache.set(imgSrc, img);
                resolve(img);
            };
            img.src = imgSrc;
        });
    }

    /**
     * 绘制图片的逻辑
     * @param {string} iconSrc 图片的地址
     * @param {IconPosition} position 绘制图片的位置
     */
    public async drawIcon(iconSrc: string, position: IconPosition): Promise<void> {
        if (!this.canvas || !this.ctx) {
            return;
        }

        const image = await this.preloadImage(iconSrc);

        this.ctx.drawImage(image, position.x, position.y, this.size, this.size);
    }

    /**
     * 清除画布内容的方法
     */
    public clearCanvas(): void {
        if (!this.canvas || !this.ctx) {
            return;
        }
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }

    /**
     * 开始绘制图片动画的方法
     * @param {string} iconSrc 绘制的图片的地址
     * @param {IconPosition[]} positions 初始绘制图片的位置, 会循环绘制, 有多少个就会绘制多少个元素
     */
    public async startAnimation(
        iconSrc: string,
        positions: IconPosition[],
        duration = 5000,
    ): Promise<void> {
        if (!this.canvas) {
            return;
        }
        // 先停止当前动画
        this.stopAnimation(false);
        // 记录当前动画起始绘制时间和持续时间
        this.startTime = performance.now();
        this.animationDuration = duration;
        // 预加载图片
        this.cachedImage = await this.preloadImage(iconSrc);

        // 初始化动画状态
        this.animationStates = positions.map(pos => ({
            x: pos.x,
            y: pos.y,
            originalY: pos.y,
            rotation: 0,
        }));

        // 动画帧函数
        const animate = (currentTime: number): void => {
            if (currentTime - this.lastDrawTime < this.frameInterval) {
                this.animationFrameId = requestAnimationFrame(animate);
                return;
            }
            if (!this.offscreenCtx) {
                return;
            }
            this.lastDrawTime = currentTime;
            this.clearCanvas();
            this.offscreenCtx.clearRect(
                0,
                0,
                this.offscreenCanvas!.width,
                this.offscreenCanvas!.height,
            );
            if (
                this.animationDuration > 0 &&
                currentTime - this.startTime >= this.animationDuration
            ) {
                this.stopAnimation();
                return;
            }

            // 获取当前的透明度
            const opacity = this.getFadeOpacity(currentTime);

            if (this.ctx) {
                this.ctx.save();
                this.ctx.globalAlpha = opacity;
                this.animationStates.forEach(state => {
                    this.updatePosition(state, this);
                    this.ctx!.clearRect(0, 0, this.canvas!.width, this.canvas!.height);
                    this.ctx!.drawImage(this.offscreenCanvas!, 0, 0);
                });
                this.ctx.restore();
            }

            this.animationFrameId = requestAnimationFrame(animate);
        };

        this.animationFrameId = requestAnimationFrame(animate);
    }

    /**
     * 得到渐隐的透明度
     * @param currentTime RAF得到的当前时间
     */
    private getFadeOpacity(currentTime: number): number {
        // 开始渐入时间为200ms
        const fadeInDuration = 200;
        // 结束渐出时间保持500ms
        const fadeOutDuration = 500;

        // 计算从动画开始经过的时间
        const elapsedTime = currentTime - this.startTime;
        // 处理开始阶段的渐入效果
        if (elapsedTime <= fadeInDuration) {
            // 计算渐入进度 (0-1)
            const fadeInProgress = elapsedTime / fadeInDuration;
            // 返回透明度 (0-1)
            return Math.min(1, fadeInProgress);
        }
        const fadeOutStartTime = this.startTime + this.animationDuration - fadeOutDuration;
        if (currentTime >= fadeOutStartTime) {
            // 计算渐隐进度 (0-1)
            const fadeProgress = (currentTime - fadeOutStartTime) / 500;
            // 返回透明度 (1-0)
            return 1 - Math.min(1, fadeProgress);
        }
        return 1; // 非渐隐阶段保持完全不透明
    }

    /**
     * 停止动画的方法, 同时也会清空画布
     */
    public stopAnimation(isTrig = true): void {
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = undefined;
        }
        isTrig && this.onAnimationEnd && this.onAnimationEnd();
        this.clearCanvas();
        this.animationStates = [];
    }

    /**
     * 获取动画状态的方法
     */
    public getAnimationStates(): AnimationState[] {
        return [...this.animationStates];
    }

    /**
     * SpecialEffectDrawer释放资源
     */
    public dispose(): void {
        this.clearCanvas();
        this.imgCache.clear();
        this.canvas = null;
        this.ctx = null;
    }

    /**
     * 用于设置类内部的canvas对象以及context对象
     * @param {HTMLCanvasElement} canvas Canvas元素
     */
    private setCanvas(canvas: HTMLCanvasElement): void {
        this.canvas = canvas;
        this.ctx = canvas.getContext("2d");
        // 获取父级元素
        const parent = canvas.parentElement;
        if (!parent) {
            return;
        }

        // 使用父级的实际尺寸
        const { clientWidth, clientHeight } = parent;

        // 设置 canvas 的显示和渲染尺寸一致
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.width = clientWidth;
        canvas.height = clientHeight;

        this.offscreenCanvas = document.createElement("canvas");
        this.offscreenCanvas.width = canvas.width;
        this.offscreenCanvas.height = canvas.height;
        this.offscreenCtx = this.offscreenCanvas.getContext("2d");

        if (this.ctx) {
            this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        }
    }

    /**
     * 用于更新canvas元素
     * @param {HTMLCanvasElement} canvas Canvas元素
     */
    public updateCanvas(canvas: HTMLCanvasElement): void {
        this.setCanvas(canvas);
    }

    /**
     * 用于更新绘制图片的大小
     * @param {number} size 图片大小, 构造函数中默认不传是80
     */
    public setSize(size: number): void {
        this.size = size;
    }

    /**
     * 用于更新绘制图片动画的移动速率, 若自定了UpdatePositionFn, 则可以忽视
     * @param {number} speed 图片动画的移动速率, 构造函数中默认不传是1.7
     */
    public setSpeed(speed: number): void {
        this.speed = speed * 2;
    }

    /**
     * 用于自定义绘制图片动画的逻辑, 默认是根据speed向上移动
     * @param {UpdatePositionFn} fn 自定义的更新位置函数
     */
    public setUpdatePositionFn(fn: UpdatePositionFn): void {
        this.updatePosition = fn;
    }

    /**
     * 处理动画结束的回调
     * @param fn 处理当动画结束的函数
     */
    public setOnAnimationEnd(fn: () => void): void {
        this.onAnimationEnd = fn;
    }
}
